import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Search, ChevronRight, Dumbbell, Hash, Timer, Milestone, Heart } from 'lucide-react';
import Layout from '../components/Layout';
import { getExercises, createExercise } from '../utils/api';

const ExerciseLibrary = () => {
  const [exercises, setExercises] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      const response = await getExercises();
      setExercises(response.data);
    } catch (error) {
      console.error('Failed to fetch exercises', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExercise = async () => {
    try {
      const response = await createExercise({
        title: 'New Exercise',
        subtitle: '',
        description: '',
        activity_log_config: JSON.stringify({ weight: true, reps: true })
      });
      if (response.data.success) {
        fetchExercises();
        // Optionally navigate to the editor immediately
        if (response.data.exercise && response.data.exercise.id) {
           navigate(`/exercises/${response.data.exercise.id}`);
        }
      }
    } catch (error) {
      console.error('Failed to create exercise', error);
    }
  };

  const getExerciseIcon = (exercise) => {
    try {
      const config = JSON.parse(exercise.activity_log_config || '{}');
      if (config.weight) return <Dumbbell size={20} />;
      if (config.reps) return <Hash size={20} />;
      if (config.time) return <Timer size={20} />;
      if (config.distance) return <Milestone size={20} />;
      if (config.bpm) return <Heart size={20} />;
    } catch (e) {
      console.error('Failed to parse activity log config', e);
    }
    return <Dumbbell size={20} />;
  };

  const filteredExercises = exercises.filter(ex => 
    ex.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout title="EXERCISE LIBRARY" subtitle="Manage your movements">
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
          <input
            type="text"
            placeholder="Search exercises..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#1C1C1E] border border-[#2C2C2E] focus:border-[#A3E635] outline-none rounded-2xl px-12 py-4 transition-all"
          />
        </div>
        <button 
          onClick={handleAddExercise}
          className="bg-[#A3E635] text-black px-6 py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:scale-105 transition-transform shadow-[0_0_20px_rgba(163,230,53,0.1)]"
        >
          <Plus size={20} /> ADD EXERCISE
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#A3E635]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredExercises.map((exercise) => (
            <Link 
              key={exercise.id}
              to={`/exercises/${exercise.id}`}
              className="bg-[#1C1C1E] border border-[#2C2C2E] p-3 rounded-[24px] hover:border-slate-700 transition-all group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#2C2C2E] flex items-center justify-center text-[#A3E635]">
                    {getExerciseIcon(exercise)}
                  </div>
                  <div>
                    <h3 className="font-bold text-base tracking-tight leading-tight">{exercise.title}</h3>
                    <p className="text-slate-500 text-[10px] uppercase font-medium tracking-wider">
                      {exercise.subtitle || 'Technical sheet'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="text-slate-600 group-hover:text-[#A3E635] transition-colors" />
              </div>
            </Link>
          ))}
          
          {filteredExercises.length === 0 && (
            <div className="col-span-full text-center py-20 bg-[#1C1C1E] border border-dashed border-[#2C2C2E] rounded-[32px]">
              <p className="text-slate-500">No exercises found. Add your first one!</p>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
};

export default ExerciseLibrary;
